import {
  CloneSalesChannel,
  Configuration,
  CreateLinkedSalesChannel,
  CreateSalesChannel,
  CreateSalesChannelForOrgAndProperty201Response,
  SalesChannelsApi,
  UpdateOpeningHoursForSalesChannelRequest,
  UpdateSalesChannel,
} from '@flipdish/orgmanagement';

import { getApiEndPoint } from '../../helpers/apibase';
import { mapServerError } from '../../services/utils/serverErrorMapper';

const baseURL = getApiEndPoint();

const configuration = new Configuration({
  basePath: baseURL,
  useDefaultUserAgent: true,
});

const defaultOptions = {
  withCredentials: true,
};

const salesChannelsApi = new SalesChannelsApi(configuration);

export type CreateSalesChannelProps = {
  orgId: string;
  propertyId: string;
  salesChannel: CreateSalesChannel;
  autoAssignMenu?: string;
};
// #region createSalesChannel
export async function createSalesChannel({
  orgId,
  propertyId,
  salesChannel,
  autoAssignMenu,
}: CreateSalesChannelProps): Promise<{ data: CreateSalesChannelForOrgAndProperty201Response }> {
  try {
    return await salesChannelsApi.createSalesChannelForOrgAndProperty(
      orgId,
      propertyId,
      salesChannel,
      autoAssignMenu,
      defaultOptions
    );
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
}
// #endregion

export type UpdateSalesChannelProps = {
  orgId: string;
  propertyId: string;
  salesChannel: UpdateSalesChannel;
  salesChannelId: string;
};
// #region updateSalesChannel
export async function updateSalesChannel({
  orgId,
  propertyId,
  salesChannel,
  salesChannelId,
}: UpdateSalesChannelProps) {
  try {
    return await salesChannelsApi.updateSalesChannelById(
      orgId,
      propertyId,
      salesChannelId,
      salesChannel,
      defaultOptions
    );
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
}
// #endregion

// #region getSalesChannels
export const getSalesChannelsKey = 'getSalesChannels';
export async function getSalesChannels(values: { orgId: string; propertyId: string }) {
  const { orgId, propertyId } = values;
  try {
    return await salesChannelsApi.getSalesChannelsByOrgIdAndPropertyId(
      orgId,
      propertyId,
      ['hierarchy.linkedSalesChannelIds', 'hierarchy.primarySalesChannelId'],
      defaultOptions
    );
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
}

export async function archiveSalesChannel({
  orgId,
  propertyId,
  salesChannelId,
}: {
  orgId: string;
  propertyId: string;
  salesChannelId: string;
}) {
  try {
    await salesChannelsApi.archiveSalesChannel(orgId, propertyId, salesChannelId, defaultOptions);
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
}

export async function unarchiveSalesChannel({
  orgId,
  propertyId,
  salesChannelId,
}: {
  orgId: string;
  propertyId: string;
  salesChannelId: string;
}) {
  try {
    await salesChannelsApi.unarchiveSalesChannel(orgId, propertyId, salesChannelId, defaultOptions);
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
}

export async function publishSalesChannel({
  orgId,
  propertyId,
  salesChannelId,
}: {
  orgId: string;
  propertyId: string;
  salesChannelId: string;
}) {
  try {
    return await salesChannelsApi.publishSalesChannel(
      orgId,
      propertyId,
      salesChannelId,
      defaultOptions
    );
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
}

export async function unpublishSalesChannel({
  orgId,
  propertyId,
  salesChannelId,
}: {
  orgId: string;
  propertyId: string;
  salesChannelId: string;
}) {
  try {
    await salesChannelsApi.unpublishSalesChannel(orgId, propertyId, salesChannelId, defaultOptions);
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
}

export type CloneSalesChannelRequest = {
  orgId: string;
  propertyId: string;
  salesChannelId: string;
  cloneSalesChannel?: CloneSalesChannel;
};

export async function cloneSalesChannel({
  orgId,
  propertyId,
  salesChannelId,
  cloneSalesChannel,
}: CloneSalesChannelRequest) {
  try {
    return await salesChannelsApi.cloneSalesChannel(
      orgId,
      propertyId,
      salesChannelId,
      cloneSalesChannel,
      defaultOptions
    );
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
}

export type CreateLinkedSalesChannelRequest = {
  orgId: string;
  propertyId: string;
  salesChannelId: string;
  createLinkedSalesChannel?: CreateLinkedSalesChannel;
};

export async function createLinkedSalesChannel({
  orgId,
  propertyId,
  salesChannelId,
  createLinkedSalesChannel,
}: CreateLinkedSalesChannelRequest): Promise<{
  data: CreateSalesChannelForOrgAndProperty201Response;
}> {
  try {
    return await salesChannelsApi.createLinkedSalesChannel(
      orgId,
      propertyId,
      salesChannelId,
      createLinkedSalesChannel,
      defaultOptions
    );
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
}

export const getOpeningHoursBySalesChannelIdKey = 'getOpeningHoursBySalesChannelId';
export const getOpeningHoursBySalesChannelId = async ({
  orgId,
  propertyId,
  salesChannelId,
}: {
  orgId: string;
  propertyId: string;
  salesChannelId: string;
}) => {
  try {
    return await salesChannelsApi.getOpeningHoursBySalesChannelId(
      orgId,
      propertyId,
      salesChannelId,
      defaultOptions
    );
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
};

export const updateOpeningHoursForSalesChannel = async ({
  orgId,
  propertyId,
  salesChannelId,
  updateOpeningHoursForSalesChannelRequest,
}: {
  orgId: string;
  propertyId: string;
  salesChannelId: string;
  updateOpeningHoursForSalesChannelRequest: UpdateOpeningHoursForSalesChannelRequest;
}) => {
  try {
    return await salesChannelsApi.updateOpeningHoursForSalesChannel(
      orgId,
      propertyId,
      salesChannelId,
      updateOpeningHoursForSalesChannelRequest,
      defaultOptions
    );
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
};

export const getSalesChannelByIdKey = 'getSalesChannelById';
export const getSalesChannelById = async ({
  orgId,
  propertyId,
  salesChannelId,
  expand,
}: {
  orgId: string;
  propertyId: string;
  salesChannelId: string;
  expand?: string[];
}) => {
  try {
    return await salesChannelsApi.getSalesChannelById(
      orgId,
      propertyId,
      salesChannelId,
      expand,
      defaultOptions
    );
  } catch (e) {
    const err = await mapServerError(e);
    throw err;
  }
};

const SalesChannelService = {
  archiveSalesChannel,
  unarchiveSalesChannel,
  createSalesChannel,
  updateSalesChannel,
  getSalesChannels,
  getSalesChannelsKey,
  unpublishSalesChannel,
  publishSalesChannel,
  getOpeningHoursBySalesChannelId,
  updateOpeningHoursForSalesChannel,
  getOpeningHoursBySalesChannelIdKey,
  getSalesChannelById,
  getSalesChannelByIdKey,
  cloneSalesChannel,
  createLinkedSalesChannel,
};

export default SalesChannelService;
